import { useAuth0 } from '@auth0/auth0-react';
import cookie from 'js-cookie';
import React, { useEffect, useState, useMemo } from 'react';
import { SessionContextType, UserData, UserExtensions } from '../types/user';
import { queryClient } from '../queryClient';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { useGetUserData } from 'hooks/queries';
import { useAccessSync } from 'hooks/useAccessSync';
import { lacksAdminAccess } from 'helpers/sessionAccess';

export const SessionContext = React.createContext<SessionContextType>(
  {} as SessionContextType,
);
function reloadUser() {
  queryClient.invalidateQueries({queryKey: [QueryServerKeys.USER.GET_USER_DATA]}); // TODO: call invalidate queries on every useQuery that calls reloadUser instead of making it here
}
export function SessionProvider({ children }: any) {

  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const namespace = process.env.REACT_APP_AUTH_NAMESPACE;

  const shallowNesting = useMemo(() => {
    if (!user) return {};
    return {
      uuid: user[`${namespace}/app_metadata`]?.uuid,
      redirectTo: process.env.REACT_APP_RUN_AUTH0_LOCAL
        ? process.env.REACT_APP_URL
        : user[`${namespace}/redirect_to`],
    };
  }, [user, namespace]);

  useEffect(() => {
    if (!user) return;
    const CloudAdminSSO = user[`${namespace}/sso`];
    if (CloudAdminSSO && !cookie.get('CloudAdminSSO')) {
      const isLocalhost =
        globalThis.location.hostname === 'localhost' ||
        globalThis.location.hostname === '127.0.0.1';
      cookie.set('CloudAdminSSO', CloudAdminSSO, {
        ...(isLocalhost ? {} : { domain: process.env.REACT_APP_DOMAIN }),
        expires: 365,
        secure: !isLocalhost,
        sameSite: 'strict',
      });
    }
  }, [user, namespace]);

  const [extensions, setExtensions] = useState<UserExtensions>({
    toggleFeatures: [],
    instancesCount: 0,
  });

  const { data: result, isLoading: isGetUserPending } = useGetUserData({
    isAuthenticated,
  });

  useEffect(() => {
    if (result) {
      setExtensions({
        ...result?.data?.user,
        userDataIsSet: true,
      });
    }
  }, [result, user]);

  // Fed from the rendered session so a downgrade resets queries only after the pages it hides unmount.
  useAccessSync({
    enabled:
      isAuthenticated &&
      !!extensions.userDataIsSet &&
      !lacksAdminAccess({
        roles: extensions.roles,
        permissions: extensions.access?.permissions,
      }),
    permissions: extensions.access?.permissions,
  });

  const initialState = useMemo(() => {
      const extendedUser = {
    ...(shallowNesting as UserData),
    ...extensions,
  } as UserData;
    return {
      user: extendedUser,
      isAuthenticated,
      isLoading: isLoading || isGetUserPending,
      extensions,
      error,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      reloadUser,
    };
  }, [isAuthenticated, isLoading, isGetUserPending, extensions, error, getAccessTokenSilently, loginWithRedirect, logout, shallowNesting]);

  return (
    <SessionContext.Provider value={initialState}>
      {children}
    </SessionContext.Provider>
  );
}
